/**
 * Credit Account Detail Page.
 *
 * Full detail view for a single credit account with editable fields,
 * accrued interest display, transaction history, and payment form.
 */

import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, TrendingUp, Trash2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import {
  useCreditAccount,
  useCreditTransactions,
  useRecordPayment,
} from "@/features/finance/api";
import { ACCOUNT_TYPES } from "@/types/credit-account";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import DetailCard from "@/components/ui/DetailCard";
import type { DetailField } from "@/components/ui/DetailCard";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(v);

function CreditAccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: acct, isLoading } = useCreditAccount(id);
  const { data: transactions } = useCreditTransactions(id);
  const paymentMutation = useRecordPayment(id || "");

  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDesc, setPaymentDesc] = useState("");

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await apiClient.put(`/api/v1/credit-accounts/${id}`, data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credit-accounts"] });
      qc.invalidateQueries({ queryKey: ["credit-accounts", id] });
      toast.success("Account updated");
    },
    onError: () => toast.error("Failed to update account"),
  });

  const deleteMutation = useMutation({
    mutationFn: async () =>
      (await apiClient.delete(`/api/v1/credit-accounts/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credit-accounts"] });
      toast.success("Account deactivated");
      navigate("/procurement?tab=credit");
    },
    onError: () => toast.error("Failed to deactivate account"),
  });

  const { data: accrued } = useQuery<{
    accrued_amount: number;
    days: number;
    daily_rate: number;
    compounding_method: string;
    current_balance: number;
    interest_rate: number;
  }>({
    queryKey: ["accrued-interest", id],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/credit-accounts/${id}/accrued-interest`))
        .data,
    enabled: !!id,
  });

  const postInterestMutation = useMutation({
    mutationFn: async () =>
      (await apiClient.post(`/api/v1/credit-accounts/${id}/post-interest`))
        .data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credit-accounts", id] });
      qc.invalidateQueries({ queryKey: ["accrued-interest", id] });
      qc.invalidateQueries({ queryKey: ["credit-transactions", id] });
      toast.success("Interest posted");
    },
    onError: () => toast.error("Failed to post interest"),
  });

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentAmount) return;
    await paymentMutation.mutateAsync({
      amount: parseFloat(paymentAmount),
      description: paymentDesc || "Payment",
    });
    setPaymentAmount("");
    setPaymentDesc("");
    toast.success("Payment recorded");
  };

  if (isLoading) return <LoadingSpinner className="py-24" size="lg" />;
  if (!acct) {
    return (
      <div className="py-24 text-center">
        <p className="text-[var(--text-secondary)]">
          Credit account not found.
        </p>
      </div>
    );
  }

  const utilization = acct.credit_limit
    ? (acct.current_balance / acct.credit_limit) * 100
    : 0;
  const typeLabel =
    ACCOUNT_TYPES.find((t) => t.value === acct.account_type)?.label ||
    acct.account_type;

  return (
    <div className="page-content space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate("/procurement?tab=credit")}
            className="mt-1 text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-[var(--text-primary)]">
                {acct.name}
              </h2>
              <Badge variant={acct.is_active ? "success" : "danger"}>
                {acct.is_active ? "Active" : "Inactive"}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              {typeLabel}
              {acct.provider ? ` · ${acct.provider}` : ""}
              {acct.account_number_last4
                ? ` · ···${acct.account_number_last4}`
                : ""}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              if (window.confirm(`Deactivate "${acct.name}"?`))
                deleteMutation.mutate();
            }}
          >
            <Trash2 className="h-3.5 w-3.5" /> Deactivate
          </Button>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <Card padding="sm">
          <p className="text-xs text-[var(--text-secondary)]">Balance</p>
          <p
            className={`text-xl font-bold ${acct.current_balance > 0 ? "text-[var(--text-warning)]" : "text-[var(--text-success)]"}`}
          >
            {fmt(acct.current_balance)}
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-xs text-[var(--text-secondary)]">Credit Limit</p>
          <p className="text-xl font-bold text-[var(--text-primary)]">
            {acct.credit_limit ? fmt(acct.credit_limit) : "N/A"}
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-xs text-[var(--text-secondary)]">Interest Rate</p>
          <p className="text-xl font-bold text-[var(--text-primary)]">
            {(acct.interest_rate * 100).toFixed(2)}%
          </p>
          <p className="text-[10px] text-[var(--text-tertiary)] capitalize">
            {acct.compounding_method} compounding
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-xs text-[var(--text-secondary)]">Utilization</p>
          <p className="text-xl font-bold text-[var(--text-primary)]">
            {acct.credit_limit ? `${utilization.toFixed(1)}%` : "N/A"}
          </p>
          {acct.credit_limit && (
            <div className="mt-1 h-1.5 rounded-full bg-[var(--surface-sunken)]">
              <div
                className={`h-1.5 rounded-full ${utilization > 80 ? "bg-[var(--color-danger)]" : utilization > 50 ? "bg-[var(--color-warning)]" : "bg-[var(--color-success)]"}`}
                style={{ width: `${Math.min(utilization, 100)}%` }}
              />
            </div>
          )}
        </Card>
      </div>

      {/* Accrued Interest */}
      {accrued && accrued.accrued_amount > 0 && (
        <Card padding="sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--fill-warning)]">
                <TrendingUp className="h-4 w-4 text-[var(--text-warning)]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[var(--text-warning)]">
                  {fmt(accrued.accrued_amount)} accrued interest ({accrued.days}{" "}
                  days)
                </p>
                <p className="text-xs text-[var(--text-secondary)]">
                  {fmt(accrued.daily_rate)}/day &middot;{" "}
                  {accrued.compounding_method} compounding
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => postInterestMutation.mutate()}
              isLoading={postInterestMutation.isPending}
            >
              Post Interest
            </Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Account Details */}
        <DetailCard
          title="Account Details"
          fields={
            [
              {
                key: "name",
                label: "Name",
                value: acct.name,
                onSave: async (v) => {
                  await updateMutation.mutateAsync({ name: v });
                },
              },
              {
                key: "account_type",
                label: "Type",
                value: typeLabel,
                editable: false,
              },
              {
                key: "provider",
                label: "Provider",
                value: acct.provider,
                placeholder: "Bank name",
                onSave: async (v) => {
                  await updateMutation.mutateAsync({
                    provider: v || undefined,
                  });
                },
              },
              {
                key: "interest_rate",
                label: "Interest Rate",
                value: acct.interest_rate
                  ? `${(acct.interest_rate * 100).toFixed(2)}%`
                  : null,
                editable: false,
              },
              {
                key: "compounding",
                label: "Compounding",
                value: acct.compounding_method,
                editable: false,
                render: (v) => (
                  <span className="text-sm capitalize text-[var(--text-primary)]">
                    {String(v)}
                  </span>
                ),
              },
              {
                key: "credit_limit",
                label: "Credit Limit",
                value: acct.credit_limit ? fmt(acct.credit_limit) : "N/A",
                editable: false,
              },
            ] satisfies DetailField[]
          }
        />

        {/* Record Payment */}
        <Card>
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4">
            Record Payment
          </h3>
          <form onSubmit={handlePayment} className="space-y-3">
            <Input
              label="Amount ($)"
              type="number"
              step="0.01"
              min="0.01"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              required
              placeholder="0.00"
            />
            <Input
              label="Description"
              value={paymentDesc}
              onChange={(e) => setPaymentDesc(e.target.value)}
              placeholder="Payment description (optional)"
            />
            <Button
              type="submit"
              size="sm"
              isLoading={paymentMutation.isPending}
            >
              Record Payment
            </Button>
          </form>
        </Card>
      </div>

      {/* Transaction History */}
      <Card padding="none">
        <div className="px-4 py-3 border-b border-[var(--border-default)]">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            Transaction History
          </h3>
        </div>
        {transactions && transactions.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-default)] bg-[var(--surface-sunken)] text-left text-xs font-medium text-[var(--text-secondary)]">
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Description</th>
                <th className="px-4 py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((txn) => (
                <tr
                  key={txn.id}
                  className="border-b border-[var(--border-subtle)] last:border-0"
                >
                  <td className="px-4 py-2 text-xs text-[var(--text-tertiary)]">
                    {new Date(txn.transaction_date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2">
                    <Badge
                      variant={
                        txn.transaction_type === "payment"
                          ? "success"
                          : txn.transaction_type === "interest"
                            ? "warning"
                            : "default"
                      }
                    >
                      {txn.transaction_type}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-[var(--text-secondary)]">
                    {txn.description || "—"}
                  </td>
                  <td
                    className={`px-4 py-2 text-right font-medium ${
                      txn.transaction_type === "payment"
                        ? "text-[var(--text-success)]"
                        : "text-[var(--text-danger)]"
                    }`}
                  >
                    {txn.transaction_type === "payment" ? "-" : "+"}
                    {fmt(txn.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="px-4 py-8 text-center text-sm text-[var(--text-tertiary)]">
            No transactions yet.
          </div>
        )}
      </Card>
    </div>
  );
}

export default CreditAccountDetailPage;
